import {Inject, Injectable, Optional} from '@angular/core';
import {ICodeName, IFormatAndLogicControlError} from '@common/interfaces';
import {HttpClient} from '@angular/common/http';
import { API_PREFIX } from '@common/constants';
import { IApiPrefix } from '@common/interfaces';

@Injectable({ providedIn: 'root' })
export class BusinessObjectsDictionaryService {
  constructor(
    @Inject('env') private env: any,
    private http: HttpClient,
    @Optional() @Inject(API_PREFIX) private apiPrefix: IApiPrefix
  ) {}

  private _entrepreneurshipObjects: ICodeName[] = [];
  public get entrepreneurshipObjects(): ICodeName[] {
    return this._entrepreneurshipObjects;
  }

  private get prefix(): string {
    return this.apiPrefix?.value ?? '';
  }

  private _businessActivities: ICodeName[] = [];
  public get businessActivities(): ICodeName[] {
    return this._businessActivities;
  }

  private _taxAuthorityDepartments: ICodeName[] = [];
  public get taxAuthorityDepartments(): ICodeName[] {
    return this._taxAuthorityDepartments;
  }

  private _legalPersonActivities: ICodeName[] = [];
  public get legalPersonActivities(): ICodeName[] {
    return this._legalPersonActivities;
  }

  private _formatAndLogicControlErrors: IFormatAndLogicControlError[] = [];
  public get formatAndLogicControlErrors(): IFormatAndLogicControlError[] {
    return this._formatAndLogicControlErrors;
  }

  private get isLoaded(): boolean {
    return this.entrepreneurshipObjects.length > 0
      && this.businessActivities.length > 0
      && this.taxAuthorityDepartments.length > 0
      && this.formatAndLogicControlErrors.length > 0;
  }

  public async loadDictionaries(): Promise<void> {
    if (this.isLoaded) {
      return;
    }

    await Promise.all([
      this.getEntrepreneurshipObjects(),
      this.getBusinessActivities(),
      this.getTaxAuthorityDepartments(),
      this.getLegalPersonActivities(),
      this.getFormatAndLogicControlErrors(),
    ]);
  }

  private async getEntrepreneurshipObjects(): Promise<void> {
    try {
      this._entrepreneurshipObjects = await this.http
        .get<ICodeName[]>(this.prefix + this.env.dictionaries.entrepreneurshipObjects)
        .toPromise();
    } catch (e) {
      console.error(e);
    }
  }

  private async getBusinessActivities(): Promise<void> {
    try {
      this._businessActivities = await this.http
        .get<ICodeName[]>(this.prefix + this.env.dictionaries.businessActivities)
        .toPromise();
    } catch (e) {
      console.error(e);
    }
  }

  private async getTaxAuthorityDepartments(): Promise<void> {
    try {
      this._taxAuthorityDepartments = await this.http
        .get<ICodeName[]>(this.prefix + this.env.dictionaries.taxAuthorityDepartments)
        .toPromise();
    } catch (e) {
      console.error(e);
    }
  }

  private async getLegalPersonActivities(): Promise<void> {
    if (this.env?.dictionaries?.legalPersonActivities) {
      try {
        this._legalPersonActivities = await this.http
          .get<ICodeName[]>(this.prefix + this.env.dictionaries.legalPersonActivities)
          .toPromise();
      } catch (e) {
        console.error(e);
      }
    }
  }

  private async getFormatAndLogicControlErrors(): Promise<void> {
    if (this.env?.dictionaries?.formatAndLogicControlErrors) {
      try {
        this._formatAndLogicControlErrors = await this.http
          .get<IFormatAndLogicControlError[]>(this.prefix + this.env.dictionaries.formatAndLogicControlErrors)
          .toPromise();
      } catch (e: any) {
        console.error(e);
      }
    }
  }
}
